import { computed, ref, toValue, toRefs, reactive, toRef, inject, watch } from 'vue'
import { defineStore } from 'pinia'
import { generateStoreName } from '@/services/store-utils'
import { log } from '@/services/log'
import { useAuthStore } from './auth'
import { FilterDefinition } from '@/services/filter-definition'
import { validationRules } from '@/services/validator'
import { FileExcel, FilePdf, FileCsv, File } from '@vicons/fa'
import { renderIcon } from '@/services/render-utils'

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]
const initTime = 24 * 60 * 60 // 24 hours
const DEFAULT_SELECTED_STATUSES = ['ACTIVE']
const baseAppState = {
  pageSize: PAGE_SIZE_OPTIONS[0],
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  stickyHeader: 'calc(90vh - 74px)',
  online: true,
  showAboutModal: false,
  showResetPasswordModal: false,
  toolsConfig: null,
  toolsActivityModal: false,
  userResetInfo: null,
  showUploadsOngoingModal: false,
  uploadsOngoingInfo: null,
  showWelcomeTitle: true,
  time: '',
  screenLockState: {
    locked: false,
    lockTime: 0,
  },
  appTheme: 'light',
  navStyle: 'left',
  allowNavStyleUpdate: false,
  configuredMinRfidLength: 24,
  configuredMaxRfidLength: 24,
  uploadHistoryFields: [
    {
      key: 'username',
      title: 'NAMEE',
      sortable: false,
      visible: true,
      disabled: false,
    },
    {
      key: 'createdTimestamp',
      title: 'CREATED TIME',
      sortable: false,
      type: 'datetime',
      visible: true,
      disabled: false,
    },
    {
      key: 'inputFileName',
      sortable: true,
      title: 'FILE',
      visible: true,
    },
    { key: 'uploadStatus', sortable: true, title: 'STATUS', visible: true },
    {
      key: 'availableCount',
      sortable: true,
      title: 'AVAILABLE',
      visible: true,
    },
    {
      key: 'processedCount',
      sortable: true,
      title: 'PROCESSED',
      visible: true,
    },
    {
      key: 'completedCount',
      sortable: true,
      title: 'COMPLETED',
      visible: true,
    },
    {
      key: 'failedCount',
      sortable: true,
      title: 'FAILED',
      visible: true,
    },
    {
      key: 'rejectedCount',
      sortable: true,
      title: 'REJECTED',
      visible: true,
    },
    {
      key: 'lastModifiedTimestamp',
      sortable: true,
      title: 'LAST MODIFIED TIME',
      visible: true,
    },
    {
      key: 'requestZoneId',
      sortable: true,
      title: 'ZONE',
      visible: false,
    },
    { key: 'actions', visible: true, title: 'ACTIONS' },
  ],
  uploadOnGoingFields: [
    {
      key: 'username',
      title: 'NAMEEON',
      sortable: false,
      visible: true,
      disabled: false,
    },
    {
      key: 'createdTimestamp',
      title: 'CREATED TIME',
      sortable: false,
      type: 'datetime',
      visible: true,
      disabled: false,
    },
    {
      key: 'file.name',
      sortable: true,
      title: 'FILE',
      visible: true,
    },
    { key: 'uploadStatus', sortable: true, title: 'STATUS', visible: true },
    {
      key: 'availableCount',
      sortable: true,
      title: 'AVAILABLE',
      visible: true,
    },
    {
      key: 'successCount',
      sortable: true,
      title: 'SUCCESS',
      visible: true,
    },
    {
      key: 'processedCount',
      sortable: true,
      title: 'PROCESSED',
      visible: true,
    },
    {
      key: 'completedCount',
      sortable: true,
      title: 'COMPLETED',
      visible: true,
    },
    {
      key: 'failedCount',
      sortable: true,
      title: 'FAILED',
      visible: true,
    },
    {
      key: 'rejectedCount',
      sortable: true,
      title: 'REJECTED',
      visible: true,
    },
    { key: 'actions', visible: true, title: 'ACTIONS' },
  ],
  idbStoreRecordCounts: {},
  toastOptions: {
    title: 'Validation Error(s) Found',
    autoHideDelay: 7500,
    static: true,
    variant: 'danger',
  },
  stompStatusOptions: {
    isConnected: false,
    isToastVisible: false,
    failures: 0,
  },
  noFilterDefinition: false,
  broadcasts: [],
  tourOptions: {
    useKeyboardNavigation: true,
    debug: true,
    labels: {
      buttonSkip: 'Skip',
      buttonPrevious: 'Previous',
      buttonNext: 'Next',
      buttonStop: 'Finish',
    },
  },
}
const basePageState = {
  search: null,
  pageIndex: 0,
  pageSize: 50,
  sortBy: null,
  sortDesc: true,
  totalRows: 0,
  supportedPageFilters: {},
}
const baseFormState = {
  enabled: true,
  canSave: false,
  hasExternalId: true,
  isMultistep: false,
  fields: [],
  actionBindings: {},
  item: null,
  title: '',
  isNew: false,
  beingCopied: false,
}
const basePageConfig = {
  pageHeading: '',
  sortingOn: 'table',
  emitItems: false,
  emitUpload: false,
  allFields: [],
  type: '',
  uri: '',
  serviceUri: null,
  statusesFilterName: 'selectedStatuses',
  noActivate: false,
  noHistory: false,
  noCopy: false,
  noStatus: false,
  noDeactivate: false,
  listingModuleName: '',
  listingActivityName: '',
  formModuleName: '',
  formActivityName: '',
  noTimeline: false,
  noDelete: false,
  noEdit: false,
  noPaging: false,
  noSearch: false,
  noSort: false,
  noAdd: false,
  noSettings: false,
  noTour: false,
  noServerDownload: false,
  noColumnSelection: false,
  noProviderSorting: false,
  noProviderFiltering: false,
  noProviderPaging: false,
  uploadOptionsCsv: 'xlsx',
  downloadOptionsCsv: 'xlsx, csv',
  viewMode: 'listing',
  newRequested: false,
  showUpload: false,
  shortcutsShown: false,
  busy: false,
  filterSectionOpacity: 0.5,
  filterCount: null,
  hasShortcuts: false,
  showFilterSection: false,
  apiColumnsMapping: [],
  showFilterFormModal: false,
}

const createPageFilters = (stateRef, configRef, statusRef) => {
  return computed(() => {
    const result = {}
    const cfg = toValue(configRef)
    const state = toValue(stateRef)
    const statuses = toValue(statusRef)
    const { pageIndex: pi, pageSize: ps, search: s, sortBy: sob, sortDesc: sod, totalRows: t, ...others } = state

    if (!cfg.noProviderSorting) {
      result.sortBy = sob ?? ''
      result.sortDesc = sod ?? true
    }
    if (!cfg.noProviderFiltering) result.search = s ?? ''
    if (!cfg.noProviderPaging) {
      result.pageIndex = pi
      result.pageSize = ps
    }
    if (cfg.viewMode === 'history') result.type = cfg.type
    if (!cfg.noStatus) {
      result[cfg.statusesFilterName] = statuses
    }
    const filters = others?.filters ?? {}
    Object.keys(filters).forEach((k) => (result[k] = filters[k]))
    log(
      'Inside createPageFilters',
      `page index ${pi}`,
      `page size ${ps}`,
      `search ${s}`,
      `sort by ${sob}`,
      `sort desc ${sod}`,
      `total rows ${t}`,
      'others',
      others,
      'others.filters',
      filters,
      'result',
      result
    )
    return result
  })
}

const generateFormData = (fields, item) =>
  fields.reduce((acc, curr) => {
    const name = curr.name
    if (!acc[name]) {
      if (curr.forSubmitOnly && curr.defaultValue !== null && (item == null || item[name] == null)) {
        acc[name] = curr.defaultValue
      } else {
        acc[name] = (item ? item[name] : null) ?? curr?.defaultValue
      }
    }
    return acc
  }, {})

export const createAppStore = (storeId, state = {}, actions = {}) => {
  log(`ᛈᛖ‍ᛡᚩ‍ᚱᚦ app store ${storeId}`, state)
  const appState = reactive({
    ...baseAppState,
    ...state,
  })
  // Keep side-nav fixed even when persisted state is rehydrated.
  watch(
    () => appState.navStyle,
    (value) => {
      if (value !== 'left') appState.navStyle = 'left'
    },
    { immediate: true }
  )
  watch(
    () => appState.appTheme,
    (value) => {
      if (value !== 'light') appState.appTheme = 'light'
    },
    { immediate: true }
  )
  return defineStore(
    generateStoreName(storeId),
    () => {
      const stompConnected = () => {
        appState.stompStatusOptions.isConnected = true
        appState.stompStatusOptions.isToastVisible = false
        appState.stompStatusOptions.failures = 0
      }
      const stompDisconnected = () => {
        appState.stompStatusOptions.isConnected = false
        if (!appState.stompStatusOptions.isToastVisible) appState.stompStatusOptions.isToastVisible = true
        appState.stompStatusOptions.failures += 1
      }
      const updateIDBStoreRecordCount = ({ storeName, count }) => {
        appState.idbStoreRecordCounts[storeName] = count
      }
      const updateTheme = () => {
        appState.appTheme = 'light'
      }
      const setAboutModal = (value) => {
        appState.showAboutModal = value
      }
      const setShowWelcomeTitle = (value) => {
        appState.showWelcomeTitle.value = value
      }
      const updateScreenLock = (locked) => {
        appState.screenLockState.locked = locked
      }
      const setScreenLockTime = (payload = initTime) => {
        appState.screenLockState.lockTime = payload
      }
      const updateNavStyle = (nav) => {
        if (appState.allowNavStyleUpdate && (nav === 'top' || nav === 'left') && appState.navStyle !== nav)
          appState.navStyle = nav
      }
      return {
        ...toRefs(appState),
        stompConnected,
        updateScreenLock,
        updateNavStyle,
        setScreenLockTime,
        setAboutModal,
        stompDisconnected,
        updateIDBStoreRecordCount,
        setShowWelcomeTitle,
        updateTheme,
        ...actions,
      }
    },
    { persist: true }
  )
}

export const createModuleStore = (storeId, cfg = {}, state = {}, actions = {}, form = {}) => {
  log(`ᛈᛖ‍ᛡᚩ‍ᚱᚦ module store ${storeId}`, state)
  const {
    uri: tempUri,
    serviceUri: tempServiceUri,
    ...baseModuleConfig
  } = {
    ...basePageConfig,
    ...cfg,
  }
  const givenLen = tempUri?.length ?? 0
  const uriLen = tempUri?.startsWith('/') ? givenLen - 1 : givenLen
  if (uriLen <= 0)
    throw new Error('URI must be provided in the module config. The Store', storeId, 'Does not have URI value defined.')
  const moduleConfig = reactive({ ...baseModuleConfig })
  const moduleState = reactive({
    ...basePageState,
    ...state,
  })
  const formConfig = reactive({
    ...baseFormState,
    ...form,
  })
  log('Initial URI given to createModuleStore', tempUri)
  return defineStore(
    generateStoreName(storeId),
    () => {
      const authStore = useAuthStore()
      const selectedStatuses = ref(DEFAULT_SELECTED_STATUSES)
      const pageFilters = createPageFilters(moduleState, moduleConfig, selectedStatuses)

      const resetBaseFilters = () => {
        moduleState.search = null
        moduleState.pageIndex = 0
        moduleState.pageSize = 50
        moduleState.sortBy = null
        moduleState.sortDesc = true
        selectedStatuses.value = DEFAULT_SELECTED_STATUSES
      }
      const uri = computed(() => {
        const bmcuri = tempUri
        return bmcuri?.startsWith('/') ? bmcuri.substring(1) : bmcuri
      })
      const permissionUri = computed(() => {
        const permissionUri = tempServiceUri
        return permissionUri?.startsWith('/') ? permissionUri.substring(1) : permissionUri
      })

      const moduleName = computed(() => {
        const module = toValue(moduleConfig)
        const value = module?.listingModuleName ?? module?.formModuleName
        return value
      })

      const activityName = computed(() => {
        const module = toValue(moduleConfig)
        const value = module?.listingActivityName ?? module?.formActivityName
        return value
      })

      const serviceUri = computed(() => {
        const cfg = toValue(moduleConfig)
        return cfg.serviceUri ?? `${toValue(uri)}`
      })

      const apiService = inject('apiService')

      const addUri = computed(() => {
        const inUri = toValue(uri)
        return `${inUri}/mono?q=new`
      })

      const noAddButton = computed(() => {
        const noAdd = toValue(moduleConfig).noAdd
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'NEW')
        const flag = !(noAdd === false && (tech || canDo))
        log(`For URI '${inUri}' and user '${u}', computed noAddButton ${flag} for noAdd ${noAdd} and canDo ${canDo}.`)
        return flag
      })

      const noEditButton = computed(() => {
        const noEdit = toValue(moduleConfig).noEdit
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canEdit = authStore.canDo(inUri, 'EDIT')
        const canDetail = authStore.canDo(inUri, 'DETAILS')
        const flag = !(noEdit === false && (tech || canEdit || canDetail))
        log(
          `For URI '${inUri}' and user '${u}', computed noEditButton ${flag} for noEdit ${noEdit}, can detail ${canDetail} and canEdit ${canEdit}.`
        )
        return flag
      })

      const noActivatedButton = computed(() => {
        const noActivate = toValue(moduleConfig).noActivate
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'ACTIVATE')
        const flag = !(noActivate === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noActivatedButton ${flag} for noActivate ${noActivate} and canDo ${canDo}.`
        )
        return flag
      })

      const noDeactivatedButton = computed(() => {
        const noDeactivate = toValue(moduleConfig).noDeactivate
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'DEACTIVATE')
        const flag = !(noDeactivate === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noDeactivatedButton ${flag} for noDeactivate ${noDeactivate} and canDo ${canDo}.`
        )
        return flag
      })

      const noDeletedButton = computed(() => {
        const noDelete = toValue(moduleConfig).noDelete
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'DELETE')
        const flag = !(noDelete === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noDeletedButton ${flag} for noDelete ${noDelete} and canDo ${canDo}.`
        )
        return flag
      })

      const noHistoryButton = computed(() => {
        const noHistory = toValue(moduleConfig).noHistory
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'HISTORY')
        const flag = !(noHistory === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noHistoryButton ${flag} for noHistory ${noHistory} and canDo ${canDo}.`
        )
        return flag
      })

      const noTimelineButton = computed(() => {
        const noTimeline = toValue(moduleConfig).noTimeline
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'TIMELINE')
        const flag = !(noTimeline === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noTimelineButton ${flag} for noTimeline ${noTimeline} and canDo ${canDo}.`
        )
        return flag
      })

      const noCopyButton = computed(() => {
        const inUri = toValue(permissionUri)
        const noCopy = toValue(moduleConfig).noCopy
        const u = authStore?.user?.username
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'NEW')
        const flag = !(noCopy === false && (tech || canDo))
        log(
          `For URI '${inUri}' and user '${u}', computed noCopyButton ${flag} for noCopy ${noCopy} and canDo ${canDo}.`
        )
        return flag
      })

      const noUpload = computed(() => {
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const csv = toValue(moduleConfig).uploadOptionsCsv ?? ''
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'UPLOAD')
        const csvLengthZero = (csv?.length ?? 0) === 0
        const flag = csvLengthZero || (!tech && !canDo)
        log(
          `For URI '${inUri}' and user '${u}', computed noUpload ${flag} for upload options csv ${csv} and canDo ${canDo}. ${csvLengthZero} || (${!tech} && ${!canDo})`
        )
        return flag
      })

      const noDownload = computed(() => {
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const csv = toValue(moduleConfig).downloadOptionsCsv ?? ''
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'EXPORT')
        const flag = (csv?.length ?? 0) === 0 || (!tech && !canDo)
        log(
          `For URI '${inUri}' and user '${u}', computed noDownload ${flag} for download options csv ${csv} and canDo ${canDo}.`
        )
        return flag
      })
      const allUploadOptions = computed(() => {
        const opts = []
        if (toValue(noUpload) !== true) {
          opts.push(
            {
              label: 'Upload File',
              key: 'uploadFile',
              icon: renderIcon(File),
            },
            {
              label: 'Download Excel Template',
              key: 'downloadExcelTemplate',
              icon: renderIcon(File),
            },
            {
              label: 'Upload History',
              key: 'uploadHistory',
              icon: renderIcon(File),
            },
            {
              label: 'Upload OnGoing',
              key: 'ongoingUploads',
              icon: renderIcon(File),
            }
          )
        }
        return opts
      })
      const allDownloadOptions = computed(() => {
        const opts = []
        if (toValue(noDownload) !== true) {
          opts.push({
            label: 'MS Excel',
            key: 'xlsx',
            icon: renderIcon(FileExcel),
          })
          if (toValue(noCsvDownload) !== true) {
            opts.push({
              label: 'Delimited CSV',
              key: 'csv',
              icon: renderIcon(FileCsv),
            })
          }
          if (toValue(pdfSupported) === true) {
            opts.push({
              label: 'Print Friendly PDF',
              key: 'pdf',
              icon: renderIcon(FilePdf),
            })
          }
        }
        return opts
      })
      const tableFieldKeys = computed(() => moduleConfig.allFields.filter((d) => d.visible).map((d) => d.key))

      const noCsvDownload = computed(() => {
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const csv = toValue(moduleConfig).downloadOptionsCsv ?? ''
        const tech = authStore.isTechnician
        const canDo = authStore.canDo(inUri, 'EXPORT')
        const flag = csv?.includes('csv') || (!tech && !canDo)
        log(
          `For URI '${inUri}' and user '${u}', computed noCsvDownload ${flag} for download options csv ${csv} and canDo ${canDo}.`
        )
        return flag
      })

      const pdfSupported = computed(() => {
        const csv = toValue(moduleConfig).downloadOptionsCsv || ''
        return csv.includes('pdf')
      })

      const toggleColumnVisibility = (values) =>
        moduleConfig.allFields.forEach((d) => (d.visible = values.includes(d.key)))

      const chosenFiltersLayout = computed(
        () => toValue(moduleConfig).filtersLayout ?? import.meta.env.VUE_APP_CLIENT_FILTERS_LAYOUT ?? 'sidebar'
      )

      const hasSidebarLayout = computed(() => toValue(chosenFiltersLayout) === 'sidebar')

      const uploadUri = computed(() => `${toValue(serviceUri)}/data-uploads`)

      const tableTabTitle = computed(() => `All ${toValue(moduleConfig).type}`)

      const canDeleteDefinition = computed(() =>
        ['ROLE_TECHNICIAN', 'ROLE_ADMIN'].includes(authStore.user?.roleName ?? '')
      )
      const filtersInfo = computed(() => {
        const filters = toValue(pageFilters)
        const cfg = toValue(moduleConfig)
        const noStatus = cfg.noStatus
        const statusesFilterName = cfg.statusesFilterName
        if (noStatus !== true) filters[statusesFilterName] = toValue(selectedStatuses)
        const info = new FilterDefinition({
          definitionJson: JSON.stringify(filters),
        }).info
        const inUri = toValue(uri)
        log('For Current URI', inUri, ' page filters ', filters, ' filter info ', info)
        return info
      })

      const tourSteps = computed(() => {
        const type = moduleConfig.type
        const steps = [
          {
            target: '#listingPageToolbar',
            content: `<h5>Dynamic Page Toolbar</h5>
                  <br>This section is the report level dynamic toolbar containing the applicable shortcuts for the
                  report being viewed. The following steps will provide a more in-depth description of each of the
                  UI elements visible on this toolbar.
                  <br>Some of these are options are dynamic in nature and may not be available on all reports and/or pages.`,
          },
        ]
        if (moduleConfig.noSearch !== true)
          steps.push({
            target: '#v-step-search',
            content: `<h5>Quick Search Criteria</h5><br>You can search here for any ${type}
                  by typing in the input field and filter matching records. To start the search, just
                  press enter or you can click the tick button which is on the right side of this input field`,
          })
        if (moduleConfig.noPaging !== true) {
          steps.push({
            target: '#v-step-pageSize',
            content: `<h5>Select Number of records to show</h5><br>You may change the current page size using this option`,
          })
          steps.push({
            target: '#v-step-pagination',
            content: `<h5>Paging Support</h5> <br> These buttons let you move between the various available report pages`,
          })
        }
        if (moduleConfig.allFields?.length > 0)
          steps.push({
            target: '#v-step-columnsSelection',
            content: `<h5>Customize View</h5>
                  <br>You may customize your view by showing / hiding other supported columns.
                  <br>Only the selected columns will be visible in the report.`,
          })
        if (noAddButton.value !== true)
          steps.push({
            target: '[data-v-step="addnew"]',
            content: `<h5>Add new ${type}</h5>
                    <br> This button navigates you to a blank form where you can add new ${type} by providing all the details.`,
          })
        if (moduleConfig.noSort !== true && moduleConfig.sortingOn === 'toolbar')
          steps.push({
            target: '[data-v-step="sort"]',
            content: `<h5>Sort Columns</h5>
                  <br>This button provides you with a small popover to select and sort the columns.`,
          })
        if (noUpload.value !== true)
          steps.push({
            target: '[data-v-step="import"]',
            content: `<h5>Upload File</h5>
                  <br>This button provides you with a small dialog to browser and upload a XLSX file.
                  <br>Upon file selection, an upload request will be sent to the server along with the file selected.`,
          })
        if (noDownload.value !== true)
          steps.push({
            target: '[data-v-step="export"]',
            content: `<h5>Download Format</h5>
                  <br>This dropdown provides you with the download format for your report.
                  <br>Upon format selection, a download request will be sent in the format selected.`,
          })
        if (moduleConfig.noTour !== true)
          steps.push({
            target: '[data-v-step="guide"]',
            content: `<h5>Virtual Tour</h5>
                  <br>This button provides you with a virtual tour of the features of the page you are currently on.`,
          })
        if (moduleConfig.hasShortcuts)
          steps.push({
            target: '[data-v-step="shortcuts"]',
            content: `<h5>Available Shortcut for  ${type}(s) </h5>
                <br> By clicking this button you can see available shortcuts`,
          })
        if (moduleConfig.noSettings !== true)
          steps.push({
            target: '[data-v-step="filter"]',
            content: `<h5>Apply Filters</h5>
                  <br>This button provides you with the applicable filters for this report.
                  <br>Upon filter selection, a new report request will be sent and the contents will refresh.`,
          })
        if (moduleConfig.noEdit !== true)
          steps.push({
            target: '[data-v-step="edit"]',
            content: `<h5> Edit Details</h5>
                <br>This button navigates you to an edit form where you can edit the details of this ${type}`,
          })
        if (moduleConfig.noHistory !== true)
          steps.push({
            target: '[data-v-step="history"]',
            content: `<h5> View Update History</h5>
                <br>This button shows a dialog containing the update history of this ${type}`,
          })
        if (noCopyButton.value !== true)
          steps.push({
            target: '[data-v-step="copyAsNew"]',
            content: `<h5>Copy this ${type} as new ${type} </h5>
                <br> By clicking this button you can copy this ${type} and create a new ${type}`,
          })
        if (noActivatedButton.value !== true)
          steps.push({
            target: '[data-v-step="activate"]',
            content: `<h5>Activate the ${type}</h5>
                <br> You can activate the ${type} by clicking this button`,
          })
        if (noDeactivatedButton.value !== true)
          steps.push({
            target: '[data-v-step="deactivate"]',
            content: `<h5>Deactivate the ${type}</h5>
                <br> You can deactivate the ${type} by clicking this button`,
          })
        if (noDeletedButton.value !== true)
          steps.push({
            target: '[data-v-step="delete"]',
            content: `<h5>Delete the ${type}</h5>
                <br> You can delete the ${type} by clicking this button`,
          })
        if (noTimelineButton.value !== true)
          steps.push({
            target: '[data-v-step="timeline"]',
            content: `<h5>TimeLine of the ${type}</h5>
                  <br> Provides a peek into the time of creation and last modification of the ${type}`,
          })
        return steps
      })
      const currentPage = computed(() => moduleState.pageIndex + 1)
      const pageCount = computed(() => Math.ceil(moduleState.totalRows / moduleState.pageSize))

      const formRules = computed(() => {
        const rules = formConfig.fields.reduce((acc, curr) => {
          if (!acc[curr.name]) {
            acc[curr.name] = (curr?.rules ?? []).map(({ remoteValidator, customValidator, ...rule }) => {
              const mapped = { ...rule }
              const apiValidator = remoteValidator?.length > 0 ? validationRules[remoteValidator] : null
              if (apiValidator != null) {
                mapped.validator = apiValidator(
                  apiService,
                  toValue(uri),
                  formConfig.item?.externalId,
                  moduleConfig.type
                )
              }
              const customValidatorFunc = customValidator?.length > 0 ? validationRules[customValidator] : null
              if (customValidatorFunc != null) {
                mapped.validator = customValidatorFunc
              }
              return mapped
            })
          }
          return acc
        }, {})
        return rules
      })
      const formData = ref(generateFormData(formConfig.fields, formConfig.item))
      {
        const tech = authStore.isTechnician
        const inUri = toValue(permissionUri)
        const u = authStore?.user?.username
        const prefix = `For URI ${inUri} and user ${u}, `
        const noAdd = toValue(noAddButton)
        const noEdit = toValue(noEditButton)
        const num = formConfig.fields?.length
        const fce = (noAdd !== true || noEdit !== true) && num > 0
        formConfig.enabled = fce
        log(`${prefix} setting form config enabled ${fce} for noAdd ${noAdd}, noEdit ${noEdit} and fields count ${num}`)
        const canEdit = authStore.canDo(inUri, 'EDIT')
        const canAdd = authStore.canDo(inUri, 'NEW')
        const cs = tech || canAdd || canEdit
        formConfig.canSave = cs
        if (!formConfig.beingCopied && formConfig.hasExternalId) {
          formData.value.externalId = formConfig?.item?.externalId
        }
        log(`${prefix} setting form can save ${cs} for canAdd ${canAdd}, canEdit ${canEdit} and tech ${tech}`)
        watch(
          () => formConfig.item,
          (newItem) => {
            const data = generateFormData(formConfig.fields, newItem)
            if (newItem?.externalId) {
              data.externalId = newItem.externalId
            }
            formConfig.isNew = newItem?.externalId == null
            formConfig.title = (formConfig.isNew ? 'Create ' : 'Edit ') + moduleConfig.type
            formData.value = data
          },
          {
            immediate: true,
          }
        )
        watch(
          () => toValue(pageFilters),
          (newFilters, oldFilters) => {
            const newIx = newFilters?.pageIndex ?? 0
            const oldIx = oldFilters?.pageIndex ?? 0
            if (oldIx === newIx && oldIx > 0) moduleState.pageIndex = 0
          },
          {
            immediate: true,
            deep: true,
          }
        )
      }
      const transform =
        actions?.transform ??
        ((item) => {
          return item
        })
      const copy =
        actions?.copy ??
        ((item) => {
          const { internalId, id, externalId, ...others } = Object.keys(item)
          const copied = {}
          log(
            'For auto copy operation of URI',
            moduleConfig.uri,
            'Excluding internalId',
            internalId,
            'ID',
            id,
            'externalId',
            externalId,
            'from result.'
          )
          formConfig.fields.forEach((fld) => {
            if (Object.keys(others).includes(fld.name)) copied[fld.name] = item[fld.name]
          })
          return copied
        })
      const module = {
        ...toRefs(moduleConfig),
        ...toRefs(moduleState),
        form: toRef(formConfig),
        formRules,
        formData,
        selectedStatuses,
        pageFilters,
        addUri,
        uploadUri,
        serviceUri,
        uri,
        permissionUri,
        toggleColumnVisibility,
        noAdd: noAddButton,
        noEdit: noEditButton,
        noActivate: noActivatedButton,
        noDeactivate: noDeactivatedButton,
        noDelete: noDeletedButton,
        noHistory: noHistoryButton,
        noTimeline: noTimelineButton,
        noCopy: noCopyButton,
        noUpload,
        allDownloadOptions,
        allUploadOptions,
        tableFieldKeys,
        noDownload,
        noCsvDownload,
        pdfSupported,
        chosenFiltersLayout,
        hasSidebarLayout,
        tableTabTitle,
        currentPage,
        pageCount,
        canDeleteDefinition,
        filtersInfo,
        tourSteps,
        copy,
        transform,
        moduleName,
        activityName,
        resetBaseFilters,
        ...actions,
      }
      return module
    },
    { persist: true }
  )
}
